import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../Dialog';
import { Input } from '../Input';
import { Label } from '../Label';
import { Button } from '../Button';

export interface EditSeriesDescriptionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (newDescription: string) => void;
  seriesNumber: string | number;
  currentDescription: string;
  isLoading?: boolean;
}

export const EditSeriesDescriptionDialog: React.FC<EditSeriesDescriptionDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  seriesNumber,
  currentDescription,
  isLoading = false,
}) => {
  const [newDescription, setNewDescription] = useState(currentDescription);

  const handleSave = () => {
    if (newDescription.trim()) {
      onSave(newDescription.trim());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isLoading) {
      e.preventDefault();
      handleSave();
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={open => {
        if (!open && !isLoading) {
          onClose();
        }
      }}
    >
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit Series Description</DialogTitle>
          <DialogDescription>
            Update the description for this series. Changes will be saved to the PACS.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label
              htmlFor="series-number"
              className="text-right"
            >
              Series Number
            </Label>
            <Input
              id="series-number"
              value={seriesNumber}
              disabled
              className="col-span-3"
            />
          </div>

          <div className="grid grid-cols-4 items-center gap-4">
            <Label
              htmlFor="current-description"
              className="text-right"
            >
              Current
            </Label>
            <Input
              id="current-description"
              value={currentDescription}
              disabled
              className="col-span-3"
            />
          </div>

          <div className="grid grid-cols-4 items-center gap-4">
            <Label
              htmlFor="new-description"
              className="text-right"
            >
              New Description
            </Label>
            <Input
              id="new-description"
              value={newDescription}
              onChange={e => setNewDescription(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isLoading}
              className="col-span-3"
              autoFocus
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isLoading || !newDescription.trim()}
          >
            {isLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditSeriesDescriptionDialog;
